import { automationConfigSchema, focusEventCommandsToRules, focusRulesToEventCommands } from '@tunasong/schemas'
import type { EntityType, EventCommands, Rule } from '@tunasong/schemas'

type ProfileConfig = Record<string, unknown> | undefined
type EventCommandsByTypeConfig = Partial<Record<EntityType, EventCommands>>

const focusRuleIdsForType = (type: EntityType): Set<string> => new Set([`focus:${type}:onEnter`, `focus:${type}:onLeave`])

const extractRules = (config: ProfileConfig): Rule[] => automationConfigSchema.safeParse(config ?? {}).data?.rules ?? []

const legacyConfig = (config: ProfileConfig): EventCommandsByTypeConfig =>
  (config?.eventCommandsByType ?? {}) as EventCommandsByTypeConfig

/**
 * Read a profile's focus commands for one entity type. Prefers the unified
 * `rules` storage; falls back to the legacy `eventCommandsByType` map so
 * profiles saved before the migration keep working (ADR Decision 1).
 */
export const readProfileEventCommands = (config: ProfileConfig, type: EntityType): EventCommands => {
  const fromRules = focusRulesToEventCommands(type, extractRules(config))
  if (Object.keys(fromRules).length > 0) {
    return fromRules
  }
  return legacyConfig(config)[type] ?? {}
}

/**
 * Produce the next profile `config` after editing one type's focus commands:
 * this type's focus rules are replaced in `config.rules`, all other rules
 * (lifecycle + other-type focus) are preserved, and the legacy
 * `eventCommandsByType` entry for this type is migrated away in place.
 */
export const mergeProfileEventCommands = (
  config: ProfileConfig,
  type: EntityType,
  newCommands: EventCommands
): Record<string, unknown> => {
  const ids = focusRuleIdsForType(type)
  const rules = [...extractRules(config).filter(rule => !ids.has(rule.id)), ...focusEventCommandsToRules(type, newCommands)]
  const { [type]: _migrated, ...remainingLegacy } = legacyConfig(config)
  return { ...config, eventCommandsByType: remainingLegacy, rules }
}
